import { useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, ImagePlus, X, Star, StarOff, AlertTriangle, RotateCw } from "lucide-react";

const MAX = 5;
const MAX_DIM = 1600;              // downscale longest edge
const MAX_INPUT_BYTES = 40 * 1024 * 1024; // guard truly huge files

// Convert any picked/captured file (incl. iPhone HEIC/HEIF, large camera
// photos) into a downscaled JPEG data URL every browser + the backend accept.
async function processImage(file) {
  let blob = file;
  const isHeic = /heic|heif/i.test(file.type) || /\.(heic|heif)$/i.test(file.name || "");
  if (isHeic) {
    const heic2any = (await import("heic2any")).default;
    const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
    blob = Array.isArray(out) ? out[0] : out;
  }
  const url = URL.createObjectURL(blob);
  try {
    const img = await new Promise((res, rej) => {
      const i = new Image();
      i.onload = () => res(i);
      i.onerror = () => rej(new Error("decode-failed"));
      i.src = url;
    });
    let w = img.naturalWidth || img.width;
    let h = img.naturalHeight || img.height;
    if (!w || !h) throw new Error("empty-image");
    const scale = Math.min(1, MAX_DIM / Math.max(w, h));
    w = Math.round(w * scale);
    h = Math.round(h * scale);
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function PhotoManager() {
  const { user, refreshUser } = useAuth();
  const [busy, setBusy] = useState(false);          // reorder / remove / primary
  const [stage, setStage] = useState(null);         // "processing" | "uploading" | null
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);     // local preview data URL
  const [error, setError] = useState(null);
  const [dragIndex, setDragIndex] = useState(null);
  const pendingRef = useRef(null);                  // processed data URL awaiting (re)upload
  const photos = (user?.profile?.photos || []).map((x) => x.src).filter(Boolean);

  const savePhotos = async (arr) => {
    setBusy(true);
    try { await api.put("/profile", { photos: arr, photoBase64: arr[0] || "" }); await refreshUser(); return true; }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); return false; }
    finally { setBusy(false); }
  };

  const reorder = async (from, to) => {
    if (from === null || from === to) return;
    const arr = [...photos];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    if (await savePhotos(arr)) toast.success("Photos reordered");
  };

  // Upload a processed data URL. Success is declared ONLY after the backend
  // confirms the save (moderation/watermark run server-side) and the refreshed
  // profile actually contains the new photo.
  const uploadDataUrl = async (dataUrl) => {
    setError(null);
    setStage("uploading");
    setProgress(0);
    try {
      await api.put(
        "/profile",
        { photos: [...photos, dataUrl], photoBase64: photos[0] || dataUrl },
        { onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); } },
      );
      const updated = await refreshUser();
      const savedCount = (updated?.profile?.photos || []).length;
      if (savedCount <= photos.length) {
        setStage(null);
        setError("Your photo was received but not saved. It may not meet our photo guidelines — please try a clear, single-person portrait.");
        return;
      }
      setStage(null);
      setPreview(null);
      pendingRef.current = null;
      toast.success("Photo added");
    } catch (err) {
      setStage(null);
      setError(formatApiError(err.response?.data?.detail) || "Upload failed. Please check your connection and try again.");
    }
  };

  const onAdd = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (photos.length >= MAX) return toast.error(`You can add up to ${MAX} photos`);
    if (file.size > MAX_INPUT_BYTES) return toast.error("That image is too large. Please pick one under 40MB.");
    setError(null);
    setPreview(null);
    setStage("processing");
    try {
      const dataUrl = await processImage(file);
      pendingRef.current = dataUrl;
      setPreview(dataUrl);
      await uploadDataUrl(dataUrl);
    } catch {
      setStage(null);
      setError("We couldn't read that photo. Please choose another from your library or take a new one.");
    }
  };

  const retry = () => { if (pendingRef.current) uploadDataUrl(pendingRef.current); };
  const dismissError = () => { setError(null); setPreview(null); pendingRef.current = null; };
  const remove = async (i) => { if (await savePhotos(photos.filter((_, idx) => idx !== i))) toast.success("Photo removed"); };
  const makePrimary = async (i) => { if (i === 0) return; if (await savePhotos([photos[i], ...photos.filter((_, idx) => idx !== i)])) toast.success("Primary photo updated"); };

  const uploading = stage !== null;
  const disabled = busy || uploading;

  return (
    <div data-testid="photo-manager">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-semibold text-emerald-900">{photos.length}/{MAX} photos</span>
        {(busy || uploading) && <Loader2 className="h-4 w-4 animate-spin text-emerald-700" />}
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-3" data-testid="photo-error">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-500" />
          <div className="flex-1">
            <p className="text-sm text-red-700">{error}</p>
            <div className="mt-2 flex gap-2">
              {pendingRef.current && (
                <button type="button" onClick={retry} data-testid="photo-retry-btn" className="inline-flex items-center gap-1.5 rounded-full bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"><RotateCw className="h-3.5 w-3.5" /> Retry</button>
              )}
              <button type="button" onClick={dismissError} data-testid="photo-error-dismiss" className="rounded-full border border-red-300 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100">Dismiss</button>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
        {photos.map((src, i) => (
          <div key={i} draggable={!disabled}
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); reorder(dragIndex, i); setDragIndex(null); }}
            onDragEnd={() => setDragIndex(null)}
            className={`group relative aspect-[4/5] cursor-grab overflow-hidden rounded-2xl border-2 active:cursor-grabbing ${i === 0 ? "border-amber-500" : "border-emerald-900/10"} ${dragIndex === i ? "opacity-40" : ""}`} data-testid={`photo-slot-${i}`}>
            <img src={src} alt={`Photo ${i + 1}`} className="pointer-events-none h-full w-full object-cover" />
            {i === 0 && <span className="absolute left-1.5 top-1.5 flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold text-white"><Star className="h-3 w-3 fill-current" /> Primary</span>}
            <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 bg-gradient-to-t from-black/60 to-transparent p-1.5 opacity-0 transition-opacity group-hover:opacity-100">
              {i !== 0
                ? <button type="button" onClick={() => makePrimary(i)} disabled={disabled} data-testid={`photo-primary-${i}`} className="rounded-lg bg-white/90 p-1.5 text-emerald-800 hover:bg-white" title="Make primary"><StarOff className="h-3.5 w-3.5" /></button>
                : <span />}
              <button type="button" onClick={() => remove(i)} disabled={disabled} data-testid={`photo-remove-${i}`} className="rounded-lg bg-white/90 p-1.5 text-red-600 hover:bg-white" title="Remove"><X className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}

        {/* Preview + progress tile while a new photo is being processed/uploaded */}
        {uploading && (
          <div className="relative flex aspect-[4/5] items-center justify-center overflow-hidden rounded-2xl border-2 border-amber-400 bg-emerald-950/5" data-testid="photo-uploading">
            {preview && <img src={preview} alt="Selected photo preview" className="absolute inset-0 h-full w-full object-cover opacity-60" />}
            <div className="relative z-10 flex flex-col items-center gap-1.5 rounded-xl bg-white/85 px-3 py-2 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-emerald-700" />
              <span className="text-[11px] font-semibold text-emerald-900">{stage === "processing" ? "Preparing…" : `Uploading ${progress}%`}</span>
            </div>
            {stage === "uploading" && (
              <div className="absolute inset-x-0 bottom-0 z-10 h-1.5 bg-black/10"><div className="h-full bg-amber-500 transition-all" style={{ width: `${progress}%` }} /></div>
            )}
          </div>
        )}

        {photos.length < MAX && !uploading && (
          <label className={`flex aspect-[4/5] cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-emerald-300 text-emerald-700 hover:bg-emerald-50 ${disabled ? "pointer-events-none opacity-50" : ""}`} data-testid="photo-upload-label">
            <ImagePlus className="h-6 w-6" />
            <span className="text-xs font-medium">Add photo</span>
            <span className="px-1 text-center text-[10px] text-emerald-600/80">Library or camera</span>
            <input type="file" accept="image/*,.heic,.heif,.heic-sequence,.heif-sequence" className="hidden" onChange={onAdd} disabled={disabled} data-testid="photo-upload-input" />
          </label>
        )}
      </div>
      <p className="mt-2 text-xs text-slate-500">Choose from your photo library or take a new one. Drag photos to reorder — your first photo is your primary. iPhone (HEIC) photos are supported.</p>
    </div>
  );
}
